import { Pill } from '@demandlocal/components'
import React, { useMemo } from 'react'
import { match } from 'ts-pattern'
import { formatCurrency, formatNumber } from '../../lib/utils/formatNumber'
import classNames from 'classnames'
import { motion } from 'framer-motion'

export default function MonthlyComparisonPerformanceCard({
  title,
  color,
  icon,
  leftSlot,
  rightSlot,
  isCurrency = false,
  isLoading = false,
}: {
  title: string
  color: 'blue' | 'purple' | 'yellow'
  icon: React.ReactElement
  leftSlot: {
    label: string
    value: number
  }
  rightSlot: {
    label: string
    value: number
  }
  isCurrency?: boolean
  isLoading?: boolean
}): JSX.Element {
  const borderColor = useMemo(() => {
    return match(color)
      .with('yellow', () => 'border-secondary-field-drab/30')
      .with('purple', () => 'border-ultra-violet/20')
      .with('blue', () => 'border-primary-glaucous/50')
      .otherwise(() => '')
  }, [color])

  const backgroundColor = useMemo(() => {
    return match(color)
      .with('yellow', () => 'bg-primary-yellow/50')
      .with('purple', () => 'bg-chrysler-blue/20')
      .with('blue', () => 'bg-primary-persian-blue')
      .otherwise(() => '')
  }, [color])

  const textColor = useMemo(() => {
    return match(color)
      .with('blue', () => 'text-white')
      .otherwise(() => 'text-primary-xiketic')
  }, [color])

  const percentAttributedChange: number = useMemo(() => {
    return rightSlot.value + leftSlot.value === 0
      ? 0
      : Math.round(
          ((rightSlot.value - leftSlot.value) / (rightSlot.value + leftSlot.value)) * 100,
        )
  }, [leftSlot.value, rightSlot.value])

  if (isLoading) {
    return (
      <motion.div className="w-full p-4 h-[214px] rounded-lg text-transparent leading-[1.4em] rounded-[4px] bg-gradient-to-r from-silver via-white-smoke to-silver bg-[length:300%] animate-shimmer" />
    )
  }

  return (
    <div
      className={`py-4 pl-4 font-Spartan rounded-lg relative flex flex-col ${backgroundColor}`}>
      <div className={`flex flex-row justify-between h-24`}>
        <span className={`font-bold text-md w-1/2 ${textColor}`}>{title}</span>
        <span className={classNames('', { 'pr-4': isCurrency })}>{icon}</span>
      </div>
      <div className={'pr-4'}>
        <div className={`grid grid-cols-2 border-t-2 ${borderColor} ${textColor}`}>
          <div className={'flex flex-col gap-2 pt-4 pl-4'}>
            <span className={'text-xs font-bold'}>{leftSlot.label}</span>
            <span className={'font-bold text-lg'}>
              {isCurrency ? formatCurrency(leftSlot.value) : formatNumber(leftSlot.value)}
            </span>
          </div>
          <div className={`flex flex-col gap-2 border-l-2 pl-4 pt-2 ${borderColor}`}>
            <div className={'flex flex-row items-center justify-between'}>
              <span className={'text-xs font-bold'}>{rightSlot.label}</span>
              <Pill
                type={
                  percentAttributedChange === 0
                    ? 'gray'
                    : percentAttributedChange > 0
                    ? 'success'
                    : 'error'
                }>
                {percentAttributedChange > 0 && '+'}
                {percentAttributedChange}%
              </Pill>
            </div>
            <span className={'font-bold text-lg'}>
              {isCurrency
                ? formatCurrency(rightSlot.value)
                : formatNumber(rightSlot.value)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
